import React, { useEffect, useState, useCallback } from 'react';
import { artifactoryService } from '../../services/artifactoryService';
import { ArtifactPicker, PickableArtifact } from '../artifacts/ArtifactPicker';
import { useTheme } from '../../contexts/ThemeContext';

interface SummonBlueprintModalProps {
  open: boolean;
  sessionId: string | null;
  onClose: () => void;
  onSummoned: () => void;   // re-reads the binding into canvas state (Task 14 refetchBlueprint)
}

/**
 * Living Blueprint SP1 — pick one of the user's OWN manifests and summon it
 * onto the current trail. Owner-scoped list (BE strict filter); binds via the
 * owner-gated route. Surfaces the 409 soft-limit + 403 non-owned errors inline.
 */
export const SummonBlueprintModal: React.FC<SummonBlueprintModalProps> = ({
  open, sessionId, onClose, onSummoned,
}) => {
  const { darkMode } = useTheme();
  const [manifests, setManifests] = useState<PickableArtifact[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setManifests(null);
    setError(null);
    artifactoryService.listOwnedManifests()
      .then((rows) => {
        if (!alive) return;
        setManifests(rows.map((r) => ({ id: r.id, name: r.name, type: 'project_manifest' })));
      })
      .catch(() => { if (alive) setError('Could not load your blueprints.'); });
    return () => { alive = false; };
  }, [open]);

  const onSelect = useCallback(async (a: PickableArtifact) => {
    if (!sessionId || busy) return;
    setBusy(true);
    setError(null);
    try {
      await artifactoryService.bindBlueprint(sessionId, a.id);
      onSummoned();
      onClose();
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) {
        setError('This trail already has a blueprint — mutate it instead.');
      } else if (status === 403) {
        setError("You don't own that blueprint.");
      } else {
        setError('Could not summon that blueprint.');
      }
    } finally {
      setBusy(false);
    }
  }, [sessionId, busy, onSummoned, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
         onClick={onClose}>
      <div
        role="dialog"
        aria-label="Summon blueprint"
        className={`w-[480px] max-h-[80vh] overflow-y-auto rounded-lg shadow-xl ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <span aria-hidden>📐</span>
            <span className="font-semibold">Summon a blueprint</span>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-600/30" aria-label="Close">✕</button>
        </div>

        <div className="p-4">
          {error && (
            <div className="mb-3 text-sm rounded px-3 py-2 bg-rose-500/15 text-rose-400">{error}</div>
          )}
          {manifests === null && !error && (
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading your blueprints…</div>
          )}
          {manifests !== null && (
            <ArtifactPicker artifacts={manifests} onSelect={onSelect} />
          )}
        </div>
      </div>
    </div>
  );
};

export default SummonBlueprintModal;
